import type { AgreementSignatory } from '@statura/utils';
import type {
  SignatureDocumentRecipientRow,
  SignatureDocumentTemplateSummaryRow,
} from '@/lib/signing/document-template-actions';

export const CREATE_SERVICE_AGREEMENT_ACTION = 'create_service_agreement' as const;

// Neutral scopes match a template to ANY funding program.
const NEUTRAL_TEMPLATE_SCOPES = new Set(['ALL', 'BOTH']);
// SAH-family aliases (a Support at Home plan accepts any of these scopes).
const SAH_TEMPLATE_SCOPES = new Set(['SUPPORT_AT_HOME', 'SAH', 'HOME_CARE']);

/**
 * The set of template program-scope tokens that are acceptable for a given care-plan
 * funding program. Neutral scopes (ALL/BOTH) always match; a SAH plan additionally
 * accepts the SAH-family aliases; every other program (HCP/CHSP/PRIVATE/...) accepts
 * its own token, and the home-care family (HCP/CHSP) also accepts a HOME_CARE-scoped
 * template. This lets a CHSP/private/HCP provider sign against a program-scoped or
 * neutral template instead of being forced to own a SAH-scoped one.
 */
function acceptableTemplateScopes(fundingProgram?: string | null): Set<string> {
  const program = String(fundingProgram ?? '')
    .trim()
    .toUpperCase();
  const acceptable = new Set<string>(NEUTRAL_TEMPLATE_SCOPES);
  if (!program || program === 'SAH' || program === 'SUPPORT_AT_HOME') {
    for (const scope of SAH_TEMPLATE_SCOPES) acceptable.add(scope);
  } else {
    acceptable.add(program);
    if (program === 'HCP' || program === 'CHSP') acceptable.add('HOME_CARE');
  }
  return acceptable;
}

export type CreateServiceAgreementAction = typeof CREATE_SERVICE_AGREEMENT_ACTION;

export type SelectionResult<T> = { success: true; value: T } | { success: false; error: string };

export function generateSahAgreementNumber(
  tz: string,
  now: Date = new Date(),
  random: () => number = Math.random,
): string {
  const ymd = now.toLocaleDateString('en-CA', { timeZone: tz });
  const yy = ymd.slice(2, 4);
  const mm = ymd.slice(5, 7);
  const rand = String(Math.floor(random() * 10000)).padStart(4, '0');
  return `SA-${yy}${mm}-${rand}`;
}

export function isAgreementReadinessBlockerKey(key: unknown): boolean {
  return key === 'service_agreement' || key === 'signatory';
}

function isSelfRecipient(row: SignatureDocumentRecipientRow): boolean {
  const relationship = String(row.recipient_relationship ?? '')
    .trim()
    .toUpperCase();
  return row.recipient_authority_type === 'SELF_CLIENT' || relationship === 'SELF';
}

export function selectAgreementSignatureRecipient(
  signatory: AgreementSignatory,
  recipients: readonly SignatureDocumentRecipientRow[],
): SelectionResult<SignatureDocumentRecipientRow> {
  if (signatory.kind === 'BLOCKED') {
    return { success: false, error: signatory.reason };
  }

  if (signatory.kind === 'PARTICIPANT') {
    const recipient = recipients.find(isSelfRecipient);
    return recipient
      ? { success: true, value: recipient }
      : {
          success: false,
          error:
            'No verified signer (the participant or an authorised representative) is available for this participant.',
        };
  }

  if (!signatory.clientContactId) {
    return {
      success: false,
      error:
        'The substitute decision-maker must be linked to a client contact before the agreement can be sent for signing.',
    };
  }

  const recipient = recipients.find(
    (row) => row.recipient_contact_id === signatory.clientContactId,
  );
  return recipient
    ? { success: true, value: recipient }
    : {
        success: false,
        error:
          'The linked substitute decision-maker is not a verified signer (the participant or an authorised representative).',
      };
}

function templateMatchesProgramScope(
  template: SignatureDocumentTemplateSummaryRow,
  acceptable: Set<string>,
): boolean {
  return (template.program_scope ?? []).some((scope) =>
    acceptable.has(String(scope).trim().toUpperCase()),
  );
}

function isUsableServiceAgreementTemplate(template: SignatureDocumentTemplateSummaryRow): boolean {
  return (
    template.resource_type === 'service_agreement' &&
    template.status === 'APPROVED' &&
    Boolean(template.current_version_id)
  );
}

export function selectServiceAgreementTemplateVersion(
  templates: readonly SignatureDocumentTemplateSummaryRow[],
  mappedTemplateId: string | null | undefined,
  // The plan's funding program. Omitted/SAH → SAH-family scope (back-compat for the
  // SAH management screen); HCP/CHSP/PRIVATE accept their own or a neutral template.
  fundingProgram?: string | null,
): SelectionResult<{
  templateId: string;
  templateVersionId: string;
  source: 'mapped' | 'default';
}> {
  const usable = templates.filter(isUsableServiceAgreementTemplate);
  const acceptable = acceptableTemplateScopes(fundingProgram);

  if (mappedTemplateId) {
    const mapped = usable.find((template) => template.id === mappedTemplateId);
    if (mapped?.current_version_id && templateMatchesProgramScope(mapped, acceptable)) {
      return {
        success: true,
        value: {
          templateId: mapped.id,
          templateVersionId: mapped.current_version_id,
          source: 'mapped',
        },
      };
    }

    if (!mapped?.current_version_id) {
      return {
        success: false,
        error:
          'The mapped client service-agreement template is not approved or has no current version.',
      };
    }
  }

  const fallback =
    usable.find((template) => templateMatchesProgramScope(template, acceptable)) ?? null;
  if (!fallback?.current_version_id) {
    return {
      success: false,
      error:
        'An approved service-agreement signing template is not available for this funding type.',
    };
  }

  return {
    success: true,
    value: {
      templateId: fallback.id,
      templateVersionId: fallback.current_version_id,
      source: 'default',
    },
  };
}
